/* eslint-disable @typescript-eslint/indent */
import Product from '@customTypes/product';
import React, { useMemo } from 'react';
import { getMonthAndDay } from '@helpers/date';
import {
  isProductUnAvailable,
  numberOfAvailableUnits,
  showAvailabilityWarning,
} from '@helpers/product';
import HotelDetails from '@customTypes/hotel-details';
import { CartItemDetails } from '@customTypes/cart-items';
import useHotSpot from '@hooks/useHotSpot';
import { format } from 'date-fns';
import useSoldOut from '@hooks/useSoldOut';
import ProductBanner from './ProductBanner';

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  loadingProducts?: boolean;
  product: Product;
};

type Banner = {
  type: 'units_left' | 'unavailable' | 'info' | 'hot_spot';
  content: string;
};

const defaultProps = {
  loadingProducts: false,
};

export default function ProductBannersFactory({
  cartData,
  date,
  loadingProducts,
  hotelDetails,
  product,
}: Props) {
  const { getDiscountText, isDateWithinVisitWindow } = useHotSpot();
  const { getSoldOutTextForProduct } = useSoldOut();

  const showAvailableUnits =
    date && showAvailabilityWarning(product, cartData) && !product.is_product_time_group;

  const showMaxOccupancy =
    !product.individual_billing && product.allow_adult && product.max_person_occupancy;

  const banners = useMemo(() => {
    const bannersBag: Banner[] = [];
    if (loadingProducts) {
      return [];
    }

    if (
      date &&
      product.is_hot_spot &&
      isDateWithinVisitWindow(date, hotelDetails) &&
      hotelDetails.hot_spot
    ) {
      bannersBag.push({
        type: 'hot_spot',
        content: `${getDiscountText(hotelDetails.hot_spot)} Off if Purchased by ${format(
          new Date(hotelDetails.hot_spot.end_booking_date),
          'MM/dd/yy',
        )}`,
      });
    }

    if (date && isProductUnAvailable(product, cartData, hotelDetails, date)) {
      bannersBag.push({
        type: 'unavailable',
        content: `${getSoldOutTextForProduct(product)} on ${getMonthAndDay(date)}`,
      });
    } else {
      if (showAvailableUnits) {
        bannersBag.push({
          type: 'units_left',
          content: `Only ${numberOfAvailableUnits(product, cartData)} Left`,
        });
      }

      if (showMaxOccupancy) {
        bannersBag.push({
          type: 'info',
          content: `${product.max_person_occupancy} Guests Included Per ${product.name}`,
        });
      }
    }

    return bannersBag;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, date, loadingProducts, hotelDetails, product]);

  return (
    <>
      {banners.map((banner, index) => (
        <ProductBanner
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          type={banner.type}
          content={banner.content}
        />
      ))}
    </>
  );
}

ProductBannersFactory.defaultProps = defaultProps;
